import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Stack,
  Chip,
  Switch,
  FormControlLabel,
  Alert,
  Button,
  CircularProgress,
  Skeleton,
  Paper,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useThemeColors } from "../../../hooks/useThemeColors";
import apiApplicationService from "../../../service/apiApplicationService";
import permissionService from "../../../service/permissionService";
import { Permission } from "../roles/types/permissions";
import { useApiApplicationGrantsPicker } from "./useApiApplicationGrantsPicker";
import ApiApplicationGrantPicker from "./ApiApplicationGrantPicker";
import { ApiApplicationGrant } from "./types";
import TokenRevealDialog from "./TokenRevealDialog";

interface ApiApplicationFormProps {
  mode: "create" | "edit";
}

const ApiApplicationForm: React.FC<ApiApplicationFormProps> = ({ mode }) => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const colors = useThemeColors();
  const accentColor = colors.primary[400];
  const headingColor = colors.primary[100];

  const [loading, setLoading] = useState(mode === "edit");
  const [permissionsLoading, setPermissionsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [hostInput, setHostInput] = useState("");
  const [hosts, setHosts] = useState<string[]>([]);
  const [wildcard, setWildcard] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [allPermissions, setAllPermissions] = useState<Permission[]>([]);
  const [grants, setGrants] = useState<Record<string, ApiApplicationGrant>>({});
  const [revealToken, setRevealToken] = useState<string | null>(null);

  useEffect(() => {
    permissionService
      .getAllPermissions()
      .then(setAllPermissions)
      .finally(() => setPermissionsLoading(false));
  }, []);

  useEffect(() => {
    if (mode !== "edit" || !id) return;
    apiApplicationService
      .get(Number(id))
      .then((app) => {
        setName(app.name);
        setDescription(app.description);
        setIsActive(app.is_active);
        if (app.allowed_hosts.includes("*")) {
          setWildcard(true);
        } else {
          setHosts(app.allowed_hosts);
        }
        const grantMap: Record<string, ApiApplicationGrant> = {};
        (app.grants || []).forEach((g) => {
          grantMap[`${g.app_label}.${g.model}`] = g;
        });
        setGrants(grantMap);
      })
      .catch(() => toast.error("Failed to load application", { position: "top-right" }))
      .finally(() => setLoading(false));
  }, [mode, id]);

  const picker = useApiApplicationGrantsPicker(allPermissions, grants, setGrants);

  const addHost = () => {
    const value = hostInput.trim();
    if (!value) return;
    if (!hosts.includes(value)) setHosts((prev) => [...prev, value]);
    setHostInput("");
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error("Name is required", { position: "top-right" });
      return;
    }
    const allowed_hosts = wildcard ? ["*"] : hosts;
    if (allowed_hosts.length === 0) {
      toast.error("Add at least one allowed host, or enable wildcard access", {
        position: "top-right",
      });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        description,
        allowed_hosts,
        is_active: isActive,
        grants: picker.grantsList,
      };
      if (mode === "create") {
        const result = await apiApplicationService.create(payload);
        toast.success(`"${result.name}" created`, { position: "top-right" });
        setRevealToken(result.token);
      } else if (id) {
        await apiApplicationService.update(Number(id), payload);
        toast.success("Application updated", { position: "top-right" });
        navigate("/configurations/api-applications");
      }
    } catch (err: any) {
      const message =
        err?.response?.data?.allowed_hosts?.[0] ||
        err?.response?.data?.name?.[0] ||
        "Failed to save application";
      toast.error(message, { position: "top-right" });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const grantedCount = picker.grantsList.length;

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 900, mx: "auto" }}>
      <Stack spacing={3}>
        <TextField
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          fullWidth
          required
          disabled={saving}
        />
        <TextField
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          fullWidth
          multiline
          minRows={2}
          disabled={saving}
        />

        {mode === "edit" && (
          <FormControlLabel
            control={<Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />}
            label={isActive ? "Active" : "Revoked"}
          />
        )}

        <Paper sx={{ boxShadow: 0, border: "1px solid", borderColor: "divider", borderRadius: 3, p: 2.5 }}>
          <Typography fontWeight={700} sx={{ mb: 0.5, color: headingColor }}>
            Allowed Hosts
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Origins this token may be used from.
          </Typography>
          <FormControlLabel
            control={<Switch checked={wildcard} onChange={(e) => setWildcard(e.target.checked)} />}
            label='Allow all origins ("*")'
          />
          {wildcard ? (
            <Alert severity="warning" sx={{ mt: 1 }}>
              Wildcard access is blocked in production — the server will reject this outside
              staging and development environments.
            </Alert>
          ) : (
            <Box sx={{ mt: 1 }}>
              <Stack direction="row" spacing={1}>
                <TextField
                  size="small"
                  placeholder="e.g. staging.example.com"
                  value={hostInput}
                  onChange={(e) => setHostInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addHost();
                    }
                  }}
                  fullWidth
                />
                <Button variant="outlined" onClick={addHost} disabled={!hostInput.trim()}>
                  Add
                </Button>
              </Stack>
              <Stack direction="row" flexWrap="wrap" mt={1} gap={1}>
                {hosts.map((host) => (
                  <Chip
                    key={host}
                    label={host}
                    onDelete={() => setHosts((prev) => prev.filter((h) => h !== host))}
                  />
                ))}
              </Stack>
            </Box>
          )}
        </Paper>

        <Box>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            gap={1}
            sx={{ mb: 1.5 }}
          >
            <Box>
              <Typography fontWeight={700} sx={{ color: headingColor }}>
                Permissions
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {grantedCount} model{grantedCount === 1 ? "" : "s"} granted
              </Typography>
            </Box>
            <Stack direction="row" gap={1}>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={picker.showEventModels}
                    onChange={(e) => picker.setShowEventModels(e.target.checked)}
                  />
                }
                label={<Typography variant="body2" color="text.secondary">Audit models</Typography>}
              />
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={picker.showSystemApps}
                    onChange={(e) => picker.setShowSystemApps(e.target.checked)}
                  />
                }
                label={<Typography variant="body2" color="text.secondary">System apps</Typography>}
              />
            </Stack>
          </Stack>
          <TextField
            size="small"
            placeholder="Search apps or models…"
            value={picker.searchTerm}
            onChange={(e) => picker.setSearchTerm(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
            disabled={permissionsLoading}
          />
          {permissionsLoading ? (
            <Stack spacing={1.5}>
              <Typography variant="caption" color="text.secondary">
                Loading models…
              </Typography>
              {[0, 1, 2, 3].map((i) => (
                <Skeleton key={i} variant="rounded" height={64} sx={{ borderRadius: 3 }} />
              ))}
            </Stack>
          ) : (
            <ApiApplicationGrantPicker
              visibleApps={picker.visibleApps}
              grants={grants}
              expandedApps={picker.expandedApps}
              toggleApp={picker.toggleApp}
              toggleVerb={picker.toggleVerb}
              accentColor={accentColor}
            />
          )}
        </Box>

        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button onClick={() => navigate("/configurations/api-applications")} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || permissionsLoading}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {saving ? "Saving…" : mode === "create" ? "Create Application" : "Save Changes"}
          </Button>
        </Stack>
      </Stack>

      <TokenRevealDialog
        token={revealToken}
        onClose={() => {
          setRevealToken(null);
          navigate("/configurations/api-applications");
        }}
      />
    </Box>
  );
};

export default ApiApplicationForm;
